import { useState } from "react";
import { NavLink } from "react-router-dom";
import {
  BookOpen, ChevronRight, Fuel, Zap, Wrench, Receipt, HeartPulse, IndianRupee,
  Sparkles, Users, ShieldCheck, Download, Car, ChevronDown,
} from "lucide-react";

const TOPICS = [
  {
    id: "add-vehicle", title: "Add a vehicle", icon: Car,
    steps: [
      "Open Garage → tap Add Vehicle.",
      "Upload the RC — RayFuel AI will parse and pre-fill the fields.",
      "Confirm make/model/variant/fuel and save.",
      "Optionally add insurance, PUC and purchase details for full TCO.",
    ],
  },
  {
    id: "log-fuel", title: "Log a fuel refill", icon: Fuel,
    steps: [
      "Tap the central + Log button → Log Fuel.",
      "Select the vehicle, take a photo of the receipt.",
      "Review the amount/rate/litres, then Save.",
      "Suggested rate for your city auto-loads — edit if the pump price is different.",
    ],
  },
  {
    id: "log-charging", title: "Log an EV charging session", icon: Zap,
    steps: [
      "+ Log → Log Charging.",
      "Pick Home or Public.",
      "Home uses your city's DISCOM tariff as the starting ₹/kWh.",
      "Public shows the operator's typical rate — override with the actual charge slip.",
    ],
  },
  {
    id: "service-repair", title: "Log service or repair", icon: Wrench,
    steps: [
      "+ Log → Log Service (for routine) or Log Repair (for breakdown).",
      "Fill in workshop, components serviced, labour + parts.",
      "Add warranty and next-service due km / date so RayFuel can remind you.",
    ],
  },
  {
    id: "upload-bill", title: "Upload a service bill", icon: Receipt,
    steps: [
      "+ Log → Upload Bill.",
      "Snap the invoice — Gemini AI extracts vendor, items and total.",
      "Review & confirm before saving. The receipt is filed in the vehicle's timeline.",
    ],
  },
  {
    id: "health", title: "Vehicle Health Score", icon: HeartPulse,
    steps: [
      "Health Score is on Insights → per-vehicle card.",
      "Score dips when mileage anomalies, repeated repairs or premature wear are detected.",
      "Ask RayFuel 'Why did my Health Score change?' for a plain-english explanation.",
    ],
  },
  {
    id: "tco", title: "Ownership cost (TCO)", icon: IndianRupee,
    steps: [
      "Vehicle Detail → Ownership Costs card toggles between Operating and TCO.",
      "TCO adds insurance amortisation + straight-line depreciation.",
      "Fill in purchase price, purchase date, insurance and depreciation to sharpen the number.",
    ],
  },
  {
    id: "ask", title: "Ask RayFuel", icon: Sparkles,
    steps: [
      "Insights → Ask RayFuel accepts text or voice.",
      "Every answer cites the records it used (View records used expander).",
      "Grounded strictly in your tenant's data — never invents numbers.",
    ],
  },
  {
    id: "members", title: "Members &amp; permissions", icon: Users,
    steps: [
      "Settings → Members lets Owners / Admins invite drivers and accountants.",
      "Assign specific vehicles to a member so they only see and log for those.",
      "Toggle granular permissions (add fuel, upload bill, edit records, etc.).",
    ],
  },
  {
    id: "subscription", title: "Subscription plans", icon: ShieldCheck,
    steps: [
      "Free plan covers fuel + service logging + core reports.",
      "Pro unlocks AI Insights, Ownership Costs, Annual Summary and more.",
      "Tap Manage Plan (Settings → Account) to compare features.",
    ],
  },
  {
    id: "export", title: "Data export &amp; privacy", icon: Download,
    steps: [
      "Settings → Account → Data export downloads a full JSON of your tenant.",
      "Danger zone → Close account permanently deletes all your data.",
      "Legal pages (Terms, Privacy, Refund) are live at /legal/*.",
    ],
  },
];

export default function TutorialCenter() {
  const [openId, setOpenId] = useState(TOPICS[0].id);
  return (
    <div className="max-w-3xl mx-auto space-y-6" data-testid="tutorial-page">
      <div>
        <div className="ft-overline text-xs">HELP CENTRE</div>
        <h1 className="font-display text-3xl sm:text-4xl font-black tracking-tighter">Learn RayFuel</h1>
        <p className="mt-2 text-sm text-[var(--text-muted)]">Short, step-by-step tours of every module. Tap a topic to expand.</p>
      </div>

      <div className="space-y-2">
        {TOPICS.map((t) => {
          const Icon = t.icon;
          const open = openId === t.id;
          return (
            <div key={t.id} className="rounded-xl border border-[var(--border-line)] bg-[var(--bg-surface)]" data-testid={`tutorial-topic-${t.id}`}>
              <button
                onClick={() => setOpenId(open ? null : t.id)}
                className="w-full flex items-center gap-3 p-4 text-left hover:bg-[var(--hover)]"
              >
                <div className="w-9 h-9 rounded-lg bg-[#FF5A1F]/10 border border-[#FF5A1F]/30 flex items-center justify-center">
                  <Icon size={18} className="text-[#FF5A1F]" />
                </div>
                <div className="flex-1">
                  <div className="font-semibold" dangerouslySetInnerHTML={{ __html: t.title }} />
                </div>
                {open ? <ChevronDown size={16} className="text-[var(--text-muted)]"/> : <ChevronRight size={16} className="text-[var(--text-muted)]"/>}
              </button>
              {open && (
                <ol className="px-4 pb-4 pl-16 space-y-2 text-sm text-[var(--text-primary)] list-decimal">
                  {t.steps.map((s, i) => <li key={i}>{s}</li>)}
                </ol>
              )}
            </div>
          );
        })}
      </div>

      <div className="rounded-xl border border-dashed border-[var(--border-line)] p-4 text-xs text-[var(--text-muted)] flex items-center gap-2">
        <BookOpen size={14}/> Video walkthroughs coming soon. Meanwhile, tap Ask RayFuel and try “How do I add a vehicle?”
      </div>
    </div>
  );
}
